import {
  InsufficientPaymentAmountError,
  PaymentPendingError,
  Session,
  waitForSession,
} from "@paywithglide/glide-js";
import ModalBackButton from "./../components/ModalBackButton";
import TransactionCheckoutHeader from "./../components/TransactionCheckoutHeader";
import PrimaryButton from "./../components/PrimaryButton";
import { useEffect, useRef, useState } from "react";
import {
  Clock,
  MousePointerClick,
  SquareArrowOutUpRight,
  CircleCheck,
} from "lucide-react";
import { AnimatePresence } from "framer-motion";
import Alert from "./../components/Alert";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { isOverpaymentAboveThreshold } from "../lib/overpayment";
import { AmountDisplay } from "../components/AmountDisplay";
import { FeeBreakdown } from "../components/FeeBreakdown";
import { TokenConversionMessage } from "../components/TokenConversionMessage";

type Props = {
  session: Session;
  onBack: () => void;
  onSuccess: (session: Session) => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
  onOverpaymentRefund?: (session: Session) => void;
};

export default function PayWithInterac({
  session,
  onBack,
  onSuccess,
  onInsufficientPaymentAmount,
  onOverpaymentRefund,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  const [step, setStep] = useState<
    "review" | "wait_for_popup" | "wait_for_payment_tx" | "wait_for_session"
  >("review");
  const [isCopied, setIsCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");

  const popupIntervalRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();
  const didTriggerOverpaymentRef = useRef(false);

  useEffect(() => {
    return () => {
      clearInterval(popupIntervalRef.current);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = undefined;
      }
    };
  }, []);

  const waitForPaymentTx = async (session: Session) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setStep("wait_for_payment_tx");

      await waitForSession(glideConfig, {
        sessionId: session.sessionId,
        signal: abortController.signal,
        onUpdate: (updatedSession) => {
          setPaymentStatus(updatedSession.paymentStatus);

          if (updatedSession.paymentStatus === "paid") {
            onSuccess(updatedSession);
          }
          if (
            depositConfig.mode === "pay" &&
            updatedSession.paymentStatus === "pending_refund" &&
            onInsufficientPaymentAmount
          ) {
            abortController.abort();
            onInsufficientPaymentAmount(updatedSession);
          }

          // Detect overpayment - only in pay mode, only after funds arrive
          const isFunded =
            updatedSession.paymentStatus === "waiting_confirmations" ||
            updatedSession.paymentStatus === "paid";

          if (
            depositConfig.mode === "pay" &&
            isFunded &&
            onOverpaymentRefund &&
            updatedSession.paymentAmountUSD &&
            updatedSession.actualPaymentAmountUSD &&
            !didTriggerOverpaymentRef.current
          ) {
            const requiredAmount = Number(updatedSession.paymentAmountUSD);
            const paidAmount = Number(updatedSession.actualPaymentAmountUSD);

            if (isOverpaymentAboveThreshold(requiredAmount, paidAmount)) {
              didTriggerOverpaymentRef.current = true;
              onOverpaymentRefund(updatedSession);
            }
          }

          window.parent?.postMessage(
            { type: "sessionUpdate", session: updatedSession },
            "*",
          );
        },
      });
    } catch (e) {
      if (e instanceof PaymentPendingError) {
        // ignore
      } else if (
        depositConfig.mode === "pay" &&
        e instanceof InsufficientPaymentAmountError &&
        onInsufficientPaymentAmount
      ) {
        onInsufficientPaymentAmount(session);
      } else {
        setStep("review");
        logger.error(`${e}`);
      }
    }
  };

  const onContinue = () => {
    setStep("wait_for_popup");

    const width = 500;
    const height = 760;
    const top = Math.max((window.innerHeight - height) / 2, 0);
    const left = Math.max(window.innerWidth - width - 60, 0);

    const w = window.open(
      "",
      "popup",
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );

    popupIntervalRef.current = setInterval(() => {
      if (!w || w.closed) {
        clearInterval(popupIntervalRef.current);
        popupIntervalRef.current = undefined;

        waitForPaymentTx(session);
      }
    }, 1000);

    setTimeout(() => {
      if (!w) {
        return;
      }

      const url = `https://app.paytrie.com/?rightSideLabel=USDC-SX&rightSideValue=${session.paymentAmount}&addr=${session.depositAddress}&vendorId=276201`;

      w.location.href = url;
    }, 500);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (error) {
      logger.error(`Failed to copy to clipboard: ${error}`);
    }
  };

  const handleBack = () => {
    // Reset step state
    setStep("review");

    // Clear any ongoing intervals/timeouts
    clearInterval(popupIntervalRef.current);
    popupIntervalRef.current = undefined;

    // Abort any ongoing operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }

    onBack();
  };

  return (
    <>
      <ModalBackButton onClick={handleBack} />
      <div>
        {depositConfig.mode === "deposit" ? (
          <TransactionCheckoutHeader title="Review purchase" />
        ) : depositConfig.mode === "withdraw" ? (
          <TransactionCheckoutHeader title="Review withdrawal" />
        ) : (
          <TransactionCheckoutHeader title="Review" />
        )}

        {depositConfig.mode === "deposit" ||
        depositConfig.mode === "withdraw" ? (
          !depositConfig.hideSettlementCopy ? (
            <AmountDisplay
              label="You'll receive"
              amount={session.sponsoredTransactionAmount}
              currencySymbol={session.sponsoredTransactionCurrencySymbol}
              amountUSD={session.sponsoredTransactionAmountUSD}
              currencyLogoUrl={session.sponsoredTransactionCurrencyLogoUrl}
              chainLogoUrl={session.sponsoredTransactionChainLogoUrl}
            />
          ) : null
        ) : null}

        {session.paymentCurrencySymbol !==
          session.sponsoredTransactionCurrencySymbol ||
        Number(session.gasRefuelAmount) > 0 ||
        depositConfig.mode === "pay" ||
        depositConfig.mode === "call" ||
        (depositConfig.mode === "deposit" &&
          depositConfig.hideSettlementCopy) ? (
          <AmountDisplay
            label={
              depositConfig.mode === "pay"
                ? "You're paying with"
                : "You're buying"
            }
            amount={session.paymentAmount}
            currencySymbol={session.paymentCurrencySymbol}
            amountUSD={session.paymentAmountUSD}
            currencyLogoUrl={session.paymentCurrencyLogoUrl}
            chainLogoUrl={session.paymentChainLogoUrl}
            className="mt-6"
          />
        ) : null}

        {!depositConfig.hideSettlementCopy ? (
          <TokenConversionMessage
            from={{
              tokenSymbol: session.paymentCurrencySymbol,
              chainId: session.paymentChainId,
              chainName: session.paymentChainName,
            }}
            to={{
              tokenSymbol: session.sponsoredTransactionCurrencySymbol,
              chainId: session.sponsoredTransactionChainId,
              chainName: session.sponsoredTransactionChainName,
            }}
            className="mt-2"
          />
        ) : null}

        <hr className="my-4 border-0.5 border-[--color-background-secondary]" />

        <FeeBreakdown
          gasRefuelUSD={session.gasRefuelUSD}
          totalFeeUSD={session.totalFeeUSD}
          processingFeeDisclaimer="Paytrie may charge additional fees"
          recipient={
            (depositConfig.mode === "deposit" ||
              depositConfig.mode === "withdraw") &&
            depositConfig.recipient &&
            !depositConfig.hideSettlementCopy
              ? depositConfig.recipient
              : undefined
          }
          etaInSeconds={
            depositConfig.mode === "pay" ? undefined : session.etaInSeconds
          }
          showETA={depositConfig.mode !== "pay"}
          onCopyRecipient={copyToClipboard}
          isCopied={isCopied}
        />

        <PrimaryButton
          className="mt-4"
          loading={step !== "review"}
          onClick={() => onContinue()}
        >
          Continue on Paytrie
        </PrimaryButton>

        {step === "review" ? (
          <div className="mt-4">
            <Alert
              id="paytrie_review"
              icon={
                <SquareArrowOutUpRight strokeWidth={2.25} className="w-4 h-4" />
              }
              title={
                depositConfig.mode === "pay" || depositConfig.mode === "call"
                  ? "Pay with Paytrie"
                  : "Buy on Paytrie"
              }
              subtitle={`Continue on Paytrie to complete your purchase. Then, return here to finalize your ${
                depositConfig.mode === "pay" ? "transaction" : "deposit"
              }.`}
            />
          </div>
        ) : (
          <div className="mt-4">
            <AnimatePresence mode="popLayout">
              {step === "wait_for_popup" ? (
                <Alert
                  key={step}
                  icon={
                    <MousePointerClick strokeWidth={2.25} className="w-4 h-4" />
                  }
                  title={
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "Complete payment"
                      : "Complete purchase"
                  }
                  subtitle={
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "Follow the instructions in the popup window to complete your payment."
                      : "Follow the instructions in the popup window to complete your purchase. Then, return here to complete your deposit."
                  }
                />
              ) : step === "wait_for_payment_tx" &&
                paymentStatus === "unpaid" ? (
                <Alert
                  key={`${step}-unpaid`}
                  icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                  title={
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "Waiting for payment"
                      : "Waiting for confirmation"
                  }
                  subtitle={
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "We're waiting for your payment. This takes about a minute."
                      : "We're waiting for your purchase. This takes about a minute."
                  }
                />
              ) : step === "wait_for_payment_tx" &&
                paymentStatus === "waiting_confirmations" ? (
                <Alert
                  key={`${step}-confirming`}
                  icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
                  title={
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "Payment received"
                      : "Purchase received"
                  }
                  subtitle={`Confirming your ${
                    depositConfig.mode === "pay" ||
                    depositConfig.mode === "call"
                      ? "payment"
                      : "purchase"
                  }. This takes a few seconds.`}
                />
              ) : null}
            </AnimatePresence>
          </div>
        )}

        <div className="mt-2 text-center">
          <p className="text-xs text-[--color-text-secondary] italic">
            *Paytrie may charge additional fees
          </p>
        </div>
      </div>
    </>
  );
}
